import { useState } from "react";
import { formatDate } from "@/lib/dates";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LOGO_FULL } from "@/lib/api";
import { X, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Download } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Groups invoice line items by date -> docket -> charge codes (charge codes only, no notes/products/signature).
function groupByDate(lineItems = []) {
  const byDate = {};
  lineItems.forEach((l) => {
    const d = l.date || "";
    byDate[d] = byDate[d] || {};
    const dk = l.docket_number || "—";
    byDate[d][dk] = byDate[d][dk] || [];
    byDate[d][dk].push(l);
  });
  return Object.keys(byDate).sort().map((d) => ({ date: d, dockets: byDate[d] }));
}

export default function InvoicePreview({ open, onOpenChange, preview, invoiceNumber, onCreate, creating, ctaLabel = "Create Draft Invoice", creatingLabel = "Creating…", onDownloadPdf }) {
  const groups = preview ? groupByDate(preview.line_items) : [];
  const [collapsed, setCollapsed] = useState({});
  const [downloading, setDownloading] = useState(false);
  if (!preview) return null;
  const groupSubtotal = (g) => Object.values(g.dockets).flat().reduce((s, l) => s + (l.amount || 0), 0);
  const groupDockets = (g) => Object.keys(g.dockets).join(", ");
  const toggle = (d) => setCollapsed((s) => ({ ...s, [d]: !s[d] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => setCollapsed(Object.fromEntries(groups.map((g) => [g.date, true])));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">Invoice Preview</DialogTitle>
        <DialogDescription className="sr-only">Preview of the invoice as the customer will receive it, grouped by date.</DialogDescription>
        <div className="bg-navy text-white px-8 py-6 flex items-center justify-between">
          <img src={LOGO_FULL} alt="True Seal Caulking" className="h-12 object-contain" />
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-300">Tax Invoice</p>
            <p className="font-heading font-bold text-lg" data-testid="preview-invoice-number">{invoiceNumber || "INV-XXXX"}</p>
          </div>
        </div>

        <div className="p-8 space-y-6" data-testid="invoice-preview-body">
          <div className="flex justify-between text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400">Bill To</p>
              <p className="font-semibold text-navy">{preview.billing_company || preview.builder || preview.customer_name || "—"}</p>
              {preview.billing_contact && <p className="text-slate-500">{preview.billing_contact}</p>}
              {preview.billing_address && <p className="text-slate-500">{preview.billing_address}</p>}
              {preview.abn && <p className="text-slate-400 text-xs">ABN: {preview.abn}</p>}
            </div>
            <div className="text-right space-y-0.5">
              <p className="text-xs uppercase tracking-wider text-slate-400">Invoice Date</p>
              <p className="text-navy">{formatDate(preview.invoice_date)}</p>
              <p className="text-xs uppercase tracking-wider text-slate-400 pt-1">Due Date</p>
              <p className="text-navy">{formatDate(preview.due_date)} <span className="text-slate-400">({preview.payment_terms})</span></p>
              <p className="text-xs uppercase tracking-wider text-slate-400 pt-1">Billing Period</p>
              <p className="text-navy">{formatDate(preview.week_start)} – {formatDate(preview.week_end)}</p>
              {(preview.site_address || preview.project_name) && (<>
                <p className="text-xs uppercase tracking-wider text-slate-400 pt-1">Site Address</p>
                <p className="text-navy" data-testid="preview-site-address">{preview.site_address || preview.project_name}</p>
              </>)}
              {preview.po_number && (<>
                <p className="text-xs uppercase tracking-wider text-navy pt-1 font-semibold">PO / Project No.</p>
                <p className="text-navy font-bold" data-testid="preview-po-number">{preview.po_number}</p>
              </>)}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{groups.length} signed docket day{groups.length !== 1 ? "s" : ""}</p>
            <div className="flex gap-2">
              <button data-testid="preview-expand-all" onClick={expandAll} className="text-xs font-semibold text-navy hover:underline flex items-center gap-1"><ChevronsUpDown className="w-3.5 h-3.5" />Expand all</button>
              <button data-testid="preview-collapse-all" onClick={collapseAll} className="text-xs font-semibold text-slate-500 hover:underline flex items-center gap-1"><ChevronsDownUp className="w-3.5 h-3.5" />Collapse all</button>
            </div>
          </div>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            {groups.length === 0 && <p className="p-6 text-center text-slate-400 text-sm">No charge items selected.</p>}
            {groups.map((g) => {
              const isCollapsed = !!collapsed[g.date];
              return (
              <div key={g.date} className="border-b border-slate-100 last:border-0">
                <button type="button" onClick={() => toggle(g.date)} data-testid={`preview-date-${g.date}`}
                  className="w-full bg-slate-50 px-4 py-2 flex items-center justify-between text-left hover:bg-slate-100 transition-colors">
                  <span className="flex items-center gap-2 font-semibold text-navy text-sm">
                    {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    {formatDate(g.date)} <span className="font-normal text-slate-400">· Docket {groupDockets(g)}</span>
                  </span>
                  <span className="text-sm font-semibold text-navy">{money(groupSubtotal(g))}</span>
                </button>
                {!isCollapsed && Object.keys(g.dockets).map((dk) => (
                  <div key={dk} className="px-4 py-2">
                    <p className="text-xs font-semibold text-slate-500 mb-1">Docket {dk}</p>
                    <ul className="space-y-1">
                      {g.dockets[dk].map((l, i) => (
                        <li key={i} className="flex items-center justify-between text-sm text-slate-700">
                          <span>• {l.description}{l.qty ? ` (${l.qty})` : ""}</span>
                          <span className="text-slate-500">{money(l.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              );
            })}
          </div>

          <div className="flex justify-end">
            <div className="w-64 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal (ex GST)</span><span className="text-navy">{money(preview.total_amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">GST 10%</span><span className="text-navy">{money(preview.gst)}</span></div>
              <div className="flex justify-between font-bold text-navy border-t border-slate-200 pt-1 mt-1"><span>Total (inc GST)</span><span data-testid="preview-total">{money(preview.total_inc_gst)}</span></div>
            </div>
          </div>

          <p className="text-[11px] text-slate-400 text-center">Every signed Service Completion Docket remains attached to this invoice for reference.</p>

          {preview.financials && (
            <div data-testid="profitability-block" className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-navy/5 px-4 py-2 flex items-center justify-between">
                <p className="font-semibold text-navy text-sm">Profitability</p>
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">Office only · not shown to customer</p>
              </div>
              <div className="divide-y divide-slate-100">
                {preview.financials.dockets.map((d) => (
                  <div key={d.job_id} className="px-4 py-2 text-sm" data-testid={`profit-docket-${d.job_id}`}>
                    <div className="flex justify-between font-medium text-navy">
                      <span>{formatDate(d.date)} · {d.docket_number}</span>
                      <span className={d.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"} data-testid={`profit-value-${d.job_id}`}>{money(d.gross_profit)} <span className="text-xs">({d.margin}%)</span></span>
                    </div>
                    <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs">
                      <span className="text-slate-500">Revenue</span><span className="text-right text-navy">{money(d.revenue)}</span>
                      <span className="text-slate-500">Labour ({d.hours}h)</span><span className="text-right text-red-500">{d.labour_cost > 0 ? `-${money(d.labour_cost)}` : money(0)}</span>
                      <span className="text-slate-500">Materials</span><span className="text-right text-red-500">{d.material_cost > 0 ? `-${money(d.material_cost)}` : money(0)}</span>
                      <span className="text-slate-500">Overhead</span><span className="text-right text-red-500">{d.overhead_cost > 0 ? `-${money(d.overhead_cost)}` : money(0)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 px-4 py-2 text-sm space-y-0.5">
                <div className="flex justify-between"><span className="text-slate-500">Revenue</span><span className="text-navy">{money(preview.financials.total.revenue)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Labour</span><span className="text-red-500">{preview.financials.total.labour_cost > 0 ? `-${money(preview.financials.total.labour_cost)}` : money(0)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Materials</span><span className="text-red-500">{preview.financials.total.material_cost > 0 ? `-${money(preview.financials.total.material_cost)}` : money(0)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Overhead</span><span className="text-red-500">{preview.financials.total.overhead_cost > 0 ? `-${money(preview.financials.total.overhead_cost)}` : money(0)}</span></div>
                <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1"><span className="text-navy">Gross Profit</span><span data-testid="profit-total" className={preview.financials.total.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}>{money(preview.financials.total.gross_profit)} ({preview.financials.total.margin}%)</span></div>
              </div>
            </div>
          )}

          {/* FINAL TOTAL — for Admin cross-check before sending & syncing to Xero (matches invoice above) */}
          <div data-testid="preview-final-total" className="border-2 border-navy rounded-xl overflow-hidden">
            <div className="bg-navy px-4 py-2"><p className="font-heading font-bold text-white text-sm">Final Invoice Total — cross-check before sending</p></div>
            <div className="p-4 flex justify-end">
              <div className="w-72 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal (ex GST)</span><span className="text-navy" data-testid="final-subtotal">{money(preview.total_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">GST 10%</span><span className="text-navy" data-testid="final-gst">{money(preview.gst)}</span></div>
                <div className="flex justify-between text-lg font-bold text-navy border-t-2 border-navy pt-1.5 mt-1.5"><span>FINAL TOTAL (inc GST)</span><span data-testid="final-total">{money(preview.total_inc_gst)}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 p-4 flex flex-wrap gap-2 justify-end bg-slate-50">
          <Button variant="outline" data-testid="preview-return" onClick={() => onOpenChange(false)}><X className="w-4 h-4 mr-2" />Edit / Return to Ready to Bill</Button>
          {onDownloadPdf && (
            <Button variant="outline" data-testid="preview-download-pdf" disabled={downloading}
              onClick={async () => { setDownloading(true); try { await onDownloadPdf(); } finally { setDownloading(false); } }}>
              <Download className="w-4 h-4 mr-2" />{downloading ? "Preparing…" : "Download PDF"}
            </Button>
          )}
          <Button data-testid="preview-create-invoice" onClick={onCreate} disabled={creating} className="bg-navy hover:bg-navy-light">
            {creating ? creatingLabel : ctaLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
